import type { SiteBundleDto, SiteSettingsDto } from '@portfolio/shared';
import { Reveal } from '../Reveal';
import { SmartLink } from '../SmartLink';
import { Testimonials } from './Testimonials';

/**
 * The page turns dark through the brand mark: two large circles rise from both sides, leave a
 * white funnel that narrows to a point, with the logo's dot floating in it. Geometry measured
 * from the 1280px design (circles r=904 meeting at the bottom centre, dot r=77) and scaled with
 * the viewport width.
 */
function Funnel() {
  return (
    <svg
      aria-hidden
      viewBox="0 0 1280 905"
      className="pointer-events-none relative block h-auto w-full"
      preserveAspectRatio="xMidYMax meet"
    >
      <circle cx="-264" cy="905" r="904" className="fill-night" />
      <circle cx="1544" cy="905" r="904" className="fill-night" />
      <circle cx="642" cy="338" r="77" className="fill-night" />
      {/* seal the hairline where the circles meet the band below */}
      <rect x="0" y="900" width="1280" height="5" className="fill-night" />
    </svg>
  );
}

/**
 * Artwork on the left of the closing card (in Arabic too, so the right-aligned text sits on the
 * right): the logo's curve and dot, cut out in white.
 * Drawn on the 460×557 design frame, with the strip and white bands extended 400 units above and
 * below it: when the card grows taller than the design (e.g. Arabic headings need more line
 * height) it reveals more band instead of scaling the curve up and clipping the dot.
 */
function StoryArt() {
  return (
    <svg
      aria-hidden
      viewBox="0 -400 460 1357"
      preserveAspectRatio="xMinYMid slice"
      className="absolute inset-y-0 left-0 hidden h-full w-[40.8%] md:block"
    >
      <rect y="-400" width="82" height="1357" fill="#EFEFEF" />
      <path
        d="M82 -400 H 456.6 V 0 A 391 391 0 0 1 82 279 Z M82 957 H 456.6 V 557 A 391 391 0 0 0 82 278 Z"
        fill="#FFFFFF"
      />
      <circle cx="421.5" cy="279.5" r="34" fill="#FFFFFF" />
    </svg>
  );
}

function StoryCta({ s }: { s: SiteSettingsDto }) {
  return (
    <section aria-labelledby="story-cta-title" className="container-site">
      <Reveal>
        <div className="relative isolate flex overflow-hidden rounded-[28px] bg-[linear-gradient(180deg,var(--color-primary)_0%,var(--color-amber)_100%)] px-6 py-12 shadow-[0_0_120px_0_rgb(250_154_33/0.16)] sm:px-10 md:min-h-[557px] md:items-center md:rounded-[40px] md:py-16 md:pr-12 md:pl-[50.2%]">
          <StoryArt />
          <div className="relative">
            <h2
              id="story-cta-title"
              className="max-w-[5.3em] text-[clamp(2.75rem,0.4rem+7.1vw,5.875rem)] leading-[0.84] font-black tracking-[-0.01em] text-cocoa rtl:max-w-[7em] rtl:text-[clamp(2.4rem,0.4rem+5.4vw,4.5rem)]"
            >
              {s.storyCtaHeading}
            </h2>
            {s.storyCtaText && (
              <p className="mt-6 max-w-[500px] text-body text-cocoa md:mt-[38px]">
                {s.storyCtaText}
              </p>
            )}
            {/* Always the WhatsApp chat (SmartLink turns #contact into it) */}
            <SmartLink
              href="#contact"
              className="mt-8 inline-flex h-12 items-center rounded-lg bg-cocoa px-6 text-ui font-semibold text-white transition-colors hover:bg-night md:mt-[37px]"
            >
              {s.storyCtaLabel}
            </SmartLink>
          </div>
        </div>
      </Reveal>
    </section>
  );
}

/** Dark closing band: funnel transition → testimonials → "Your Story Deserves To Be Seen." */
export function Finale({ data }: { data: SiteBundleDto }) {
  const s = data.settings;
  const hasTestimonials = data.testimonials.length > 0;
  return (
    // Pulled up under the last FAQ card (which sits above it), as in the design.
    <div className="relative -mt-[17.8vw]">
      <Funnel />
      <div className="-mt-px bg-night pt-[11.7vw] pb-16 md:pt-[150px] md:pb-[76px]">
        {hasTestimonials && (
          <Testimonials heading={s.testimonialsHeading} items={data.testimonials} />
        )}
        <div className={hasTestimonials ? 'mt-12 md:mt-[63px]' : undefined}>
          <StoryCta s={s} />
        </div>
      </div>
    </div>
  );
}
